import { useCallback, useEffect, useRef, useState } from "react";
import { IsUnauthorizedError, useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import { useDebouncedValue } from "helpers/useDebouncedValue";
import { Column, GetSortString, RestoreColumnsState, SaveColumnsState } from "components/Table";

//how long the user stops typing before the list reloads
const SEARCH_DEBOUNCE_MS = 1000;
const DEFAULT_PAGE_SIZE = 25;

export interface UseResourceListOptions<T, K extends string> {
    //url is the collection endpoint, e.g. "/api/maskformats"
    url: string
    //columnsKey is the localStorage key the column layout persists under. It is
    //used for both saving and restoring, so the two cannot drift apart
    columnsKey: string
    defaultColumns: Column<K>[]
    //mapItem turns one row of the API payload into the shape the table renders
    mapItem: (raw: any) => T
    //paginated asks the server for one page at a time. Lists that fetch
    //everything, because they are short or because their rows are reorderable,
    //leave it off
    paginated?: boolean
    //sorted sends the table's sort order to the server
    sorted?: boolean
    //searchable adds the "search all fields" toggle and its parameter
    searchable?: boolean
    //extraParams are appended to every request, for filters a page adds of its
    //own. Keep the object referentially stable or the list reloads every render
    extraParams?: Record<string, string | undefined>
    //errorMessage overrides the notification shown when the fetch fails
    errorMessage?: string
}

export interface ResourceList<T, K extends string> {
    items: T[]
    //setItems lets a page apply an optimistic change, such as a drag-reorder,
    //before the server has confirmed it. Restore the previous value if the
    //request then fails
    setItems: (items: T[]) => void
    count: number
    finishedLoading: boolean

    search: string
    setSearch: (v: string) => void
    searchAllFields: boolean
    setSearchAllFields: (v: boolean) => void

    columnsState: Column<K>[]
    setColumnsState: (c: Column<K>[]) => void

    skip: number
    take: number

    //reload refetches the current page with the current search and sort
    reload: () => void
    //tableProps covers every DataTable prop that is the same on every list page
    tableProps: {
        data: T[]
        finishedLoading: boolean
        columnsState: Column<K>[]
        columnsDataId: string
        onColumnOrderChanged: (c: Column<K>[]) => void
        onSortChanged: (c: Column<K>[]) => void
        paginationStartItem?: number
        paginationItemCount?: number
        dataCount?: number
        onPaginationStartItemChanged?: (v: number) => void
        onPaginationItemCountChanged?: (v: number) => void
    }
}

//useResourceList owns everything a paginated, searchable, sortable list page
//does with the server: the request, the search box and its debounce, the column
//layout and its persistence, and the loading flag. Pages keep only what is
//actually theirs — the columns they render and what a row's menu does.
export function useResourceList<T, K extends string>(
    options: UseResourceListOptions<T, K>,
): ResourceList<T, K> {
    const login = useLogin();
    const notification = useNotification();

    const [items, setItems] = useState<T[]>([]);
    const [count, setCount] = useState(0);
    const [finishedLoading, setFinishedLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [searchAllFields, setSearchAllFields] = useState(false);

    const [skip, setSkip] = useState(0);
    const [take, setTake] = useState(DEFAULT_PAGE_SIZE);

    //the column layout is restored once, on the first load, so that a later
    //render cannot overwrite what the user has since changed
    const [columnsState, setColumnsState] = useState(
        () => RestoreColumnsState(options.columnsKey, options.defaultColumns));

    //the request is driven from a ref so that changing sort or page does not
    //have to wait for a re-render to see the new value. It holds what was last
    //asked for rather than what last committed: a second action taken while a
    //request is still in flight has to build on the first one, not undo it
    const latest = useRef({ columnsState, skip, take });

    //sort, paging and the debounced search all start a request without waiting
    //for the one in flight, so responses can arrive out of order. Only the most
    //recently started request is allowed to commit anything
    const requestSeq = useRef(0);

    const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

    const load = useCallback(async (columns: Column<K>[], nextSkip: number, nextTake: number) => {
        const seq = ++requestSeq.current;
        const superseded = () => seq !== requestSeq.current;
        const req = login.GetAxios();

        if (!req) {
            return; //not logged in, nothing to do
        }

        latest.current = { columnsState: columns, skip: nextSkip, take: nextTake };

        setFinishedLoading(false);

        try {
            const resp = await req.get(options.url, {
                params: {
                    //sortBy is read from the columns the caller passed in, not
                    //from state, so a click on a header sorts by what was just
                    //clicked rather than by the previous sort
                    sortBy: options.sorted ? GetSortString(columns) : undefined,
                    skip: options.paginated ? nextSkip : undefined,
                    take: options.paginated ? nextTake : undefined,
                    search: debouncedSearch,
                    searchAllFields: options.searchable && searchAllFields ? "true" : undefined,
                    ...options.extraParams,
                },
            });

            if (superseded()) {
                return; //a newer request is in flight; its answer is the current one
            }

            setColumnsState(columns);
            setItems((resp.data.items || []).map(options.mapItem));
            setCount(resp.data.count || 0);
            setSkip(nextSkip);
            setTake(nextTake);
        } catch (err) {
            if (superseded()) {
                return; //the request this failed for has already been replaced
            }

            //an expired session is already being handled by the interceptor in
            //the login provider, which drops the session and redirects; a
            //generic fetch error on top of that is just noise. Anything else is
            //worth telling the user about
            if (!IsUnauthorizedError(err)) {
                console.error(err);
                notification.Display({
                    title: "Error",
                    message: options.errorMessage || "Could not fetch data",
                    type: "error",
                });
            }
        }

        setFinishedLoading(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, searchAllFields, options.url, options.extraParams]);

    //a new search or filter always returns to the first page: the row the user
    //was looking at is not in the new result set
    useEffect(() => {
        load(latest.current.columnsState, 0, latest.current.take);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    useEffect(() => {
        SaveColumnsState(options.columnsKey, columnsState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsState]);

    //changeColumns is the one path that changes the layout without a request
    //(a column drag), so it has to keep the ref in step itself
    const changeColumns = useCallback((c: Column<K>[]) => {
        latest.current = { ...latest.current, columnsState: c };
        setColumnsState(c);
    }, []);

    const reload = useCallback(() => {
        const { columnsState: c, skip: s, take: t } = latest.current;
        load(c, s, t);
    }, [load]);

    return {
        items,
        setItems,
        count,
        finishedLoading,
        search,
        setSearch,
        searchAllFields,
        setSearchAllFields,
        columnsState,
        setColumnsState: changeColumns,
        skip,
        take,
        reload,
        tableProps: {
            data: items,
            finishedLoading,
            columnsState,
            columnsDataId: options.columnsKey,
            onColumnOrderChanged: changeColumns,
            onSortChanged: (c) => load(c, latest.current.skip, latest.current.take),
            ...(options.paginated ? {
                paginationStartItem: skip,
                paginationItemCount: take,
                dataCount: count,
                onPaginationStartItemChanged: (v: number) => load(latest.current.columnsState, v, latest.current.take),
                onPaginationItemCountChanged: (v: number) => load(latest.current.columnsState, latest.current.skip, v),
            } : {}),
        },
    };
}
